var _excluded = ["align", "as", "children", "className", "color", "inverse", "marginBottom", "marginLeft", "marginRight", "marginTop", "size", "variation", "weight"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { forwardRef, useMemo } from 'react';
import classnames from 'classnames';
import { getContrastRatio, useBackgroundColor } from "../BackgroundProvider";
import StyledTypography from "./styled";
import { useTheme } from 'styled-components';
var DEFAULT_MIN_COLOR_CONTRAST = 4.5;
var chooseHighestContrastRatio = function chooseHighestContrastRatio(_ref) {
  var bgColor = _ref.bgColor,
    foregroundColor = _ref.foregroundColor,
    inverseColor = _ref.inverseColor,
    _ref$threshold = _ref.threshold,
    threshold = _ref$threshold === void 0 ? 5 : _ref$threshold;
  var foregroundColorContrast = getContrastRatio(foregroundColor, bgColor);
  if (foregroundColorContrast >= threshold) {
    return foregroundColor;
  }
  var inverseColorContrast = getContrastRatio(inverseColor, bgColor);
  if (foregroundColorContrast > inverseColorContrast) {
    return foregroundColor;
  }
  return inverseColor;
};
var Typography = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var _ref2$align = _ref2.align,
    align = _ref2$align === void 0 ? 'inherit' : _ref2$align,
    _ref2$as = _ref2.as,
    as = _ref2$as === void 0 ? 'span' : _ref2$as,
    children = _ref2.children,
    className = _ref2.className,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? 'normal' : _ref2$color,
    inverseProp = _ref2.inverse,
    _ref2$marginBottom = _ref2.marginBottom,
    marginBottom = _ref2$marginBottom === void 0 ? 'none' : _ref2$marginBottom,
    _ref2$marginLeft = _ref2.marginLeft,
    marginLeft = _ref2$marginLeft === void 0 ? 'none' : _ref2$marginLeft,
    _ref2$marginRight = _ref2.marginRight,
    marginRight = _ref2$marginRight === void 0 ? 'none' : _ref2$marginRight,
    _ref2$marginTop = _ref2.marginTop,
    marginTop = _ref2$marginTop === void 0 ? 'none' : _ref2$marginTop,
    _ref2$size = _ref2.size,
    size = _ref2$size === void 0 ? 's2' : _ref2$size,
    _ref2$variation = _ref2.variation,
    variation = _ref2$variation === void 0 ? 'text' : _ref2$variation,
    _ref2$weight = _ref2.weight,
    weight = _ref2$weight === void 0 ? 'normal' : _ref2$weight,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var theme = useTheme() || {};
  var _useBackgroundColor = useBackgroundColor(),
    bgColorName = _useBackgroundColor.colorName,
    _useBackgroundColor$c = _useBackgroundColor.cssValue,
    bgColorValue = _useBackgroundColor$c === void 0 ? theme.color_background_page_default_1 : _useBackgroundColor$c;
  var inverse = inverseProp;

  /**
   * This function is memoised to reduce unnecessary calls to the `getContrastRatio()`
   * function.
   */
  var cssColor = useMemo(function () {
    var _theme$config_backgro;
    /**
     * 'inherit' allows parent DOM elements to control the colour.
     */
    if (color === 'inherit') return 'inherit';

    /**
     * If a specific colour token exists in the below format, use it. For example,
     * LLoyds has this token: 'heading_color_brand_on_color_brand1'. In this case, if the text colour
     * is 'brand' and the background colour is 'brand1' or 'overlay', then the text is displayed
     * in lime green.
     */
    if (bgColorName && theme["".concat(variation, "_color_").concat(color, "_on_color_").concat(bgColorName)]) {
      return theme["".concat(variation, "_color_").concat(color, "_on_color_").concat(bgColorName)];
    }

    /**
     * If the `inverse` prop is explicitly set to `true`, the inverse token will be used.
     */
    if (inverse) return theme.color_text_inverse_default_1;

    /**
     * If specified in tokens, invert the foreground.
     */
    if (inverse !== false && (_theme$config_backgro = theme.config_backgrounds_invert_true) !== null && _theme$config_backgro !== void 0 && _theme$config_backgro.includes(bgColorValue)) return theme.color_text_inverse_default_1;

    /**
     * Allow token to enforce non-inverse foregrounds.
     */
    if (theme["config_backgrounds_".concat(bgColorValue, "_invert")] === false) {
      inverse = false;
    }

    /**
     * If above conditions are not met, the below code calculates the colour contrast ratio
     * based on foreground color (`color` prop) and background colour (retrieved from
     * `BackgroundProvider` using the `useBackgroundColor()` hook). If the contrast ratio is
     * greater than DEFAULT_MIN_COLOR_CONTRAST then it displays the text as requested, otherwise
     * it will consider displaying the inverse colour. The colour with the greatest contrast is preferred.
     * This logic can be overridden by explicitly setting `inverse` to false.
     */
    switch (color) {
      case 'brand':
        return inverse === false ? theme.color_text_brand_1 : chooseHighestContrastRatio({
          bgColor: bgColorValue,
          foregroundColor: theme.color_text_brand_1,
          inverseColor: theme.color_text_inverse_default_1
        });
      case 'critical':
        return inverse === false ? theme.color_text_critical : chooseHighestContrastRatio({
          bgColor: bgColorValue,
          foregroundColor: theme.color_text_critical,
          inverseColor: theme.color_text_inverse_default_1
        });
      case 'subdued':
        return inverse === false ? theme.color_text_subdued : chooseHighestContrastRatio({
          bgColor: bgColorValue,
          foregroundColor: theme.color_text_subdued,
          inverseColor: theme.color_text_inverse_default_1
        });
      default:
        /**
         * Headings and Display Text should use token Text Default 2. Other typography
         * should use Text Default 1
         */
        return variation === 'display' || variation === 'heading' ? inverse === false ? theme.color_text_default_2 : chooseHighestContrastRatio({
          bgColor: bgColorValue,
          foregroundColor: theme.color_text_default_2,
          inverseColor: theme.color_text_inverse_default_1
        }) : inverse === false ? theme.color_text_default_1 : chooseHighestContrastRatio({
          bgColor: bgColorValue,
          foregroundColor: theme.color_text_default_1,
          inverseColor: theme.color_text_inverse_default_1
        });
    }
  }, [bgColorName, bgColorValue, color, inverse, theme]);
  return /*#__PURE__*/React.createElement(StyledTypography, _extends({
    as: as,
    className: classnames('csl-typography', "csl-".concat(variation, "-").concat(size), className),
    $align: align,
    $color: cssColor,
    $marginBottom: marginBottom,
    $marginLeft: marginLeft,
    $marginRight: marginRight,
    $marginTop: marginTop,
    $size: size,
    $variation: variation,
    $weight: weight,
    ref: ref
  }, rest), children);
});
Typography.displayName = 'Typography';
export default Typography;